import React, { useEffect } from "react";
import {
    InputProps,
    TextField,
    TextFieldProps,
    FormHelperText,
} from "@mui/material";

type TDefaultTextField<T> = {
    name: string;
    label: string;
    value: string;
    props?: InputProps & TextFieldProps;
    small?: boolean;
    priceNumber: number | undefined;
    setPriceNumber: React.Dispatch<React.SetStateAction<number | undefined>>;
    error?: boolean | undefined;
    helperText?: React.ReactNode;
    type?: React.HTMLInputTypeAttribute;
    onBlur?: React.FocusEventHandler<HTMLInputElement | HTMLTextAreaElement>;
    onChange?: React.ChangeEventHandler<HTMLInputElement | HTMLTextAreaElement>;
    handleFormattedChange?: (value: string) => void;
    style?: React.CSSProperties | undefined;
    disabled?: boolean | undefined;
    maxLength?: number | undefined;
    formik?: any;
    typeLucro?: string;
    priceCost?: number | undefined;
    resalePrice?: number | undefined;
    onKeyDown?: (event: React.KeyboardEvent<HTMLDivElement>) => void
    inputRef?: React.RefObject<HTMLInputElement>
}

function PriceTextField<T>({
    name,
    label,
    value,
    props,
    helperText,
    error,
    type,
    onBlur,
    onChange,
    handleFormattedChange,
    style,
    priceNumber,
    setPriceNumber,
    disabled,
    maxLength,
    formik,
    typeLucro,
    priceCost,
    resalePrice,
    onKeyDown,
    inputRef
}: TDefaultTextField<T>) {

    const calcularPercentualLucro = (costPrice: number | undefined, sellingPrice: number): number => {
        if (costPrice) {
            const lucroPercentual = ((sellingPrice - costPrice) / costPrice) * 100;
            return lucroPercentual < 0 ? 0 : Number(lucroPercentual.toFixed(2));
        }
        return 0;
    };
    
    // onChange para atualizar o valor do campo de texto.
    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const inputValue = e.target.value;
        const numericValue = Number(inputValue.replace(/\D/g, "")) / 100;
        setPriceNumber(numericValue);

        if (formik && typeLucro) {
            let lucroPercentual = 0;

            if (priceCost !== undefined) {
                lucroPercentual = calcularPercentualLucro(priceCost, numericValue);
            } else if (resalePrice !== undefined) {
                lucroPercentual = calcularPercentualLucro(resalePrice, numericValue);
            }

            formik.setFieldValue(typeLucro, lucroPercentual);
        }

        if (handleFormattedChange) {
            handleFormattedChange(inputValue);
        } else if (onChange) {
            onChange(e);
        }
    };

    // Formata o valor a ser exibido no campo de texto.
    const formattedDisplayValue = (priceNumber || 0).toLocaleString("pt-BR", {
        style: "currency",
        currency: "BRL",
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    }).replace("R$", "")

    return (
        <TextField
            disabled={disabled}
            helperText={
                error && (
                    <FormHelperText sx={{ margin: -0.5, padding: 0 }} error={error}>
                        {String(helperText)}
                    </FormHelperText>
                )
            }
            onChange={handleInputChange}
            onBlur={onBlur}
            type={type ? type : undefined}
            variant="outlined"
            size="small"
            autoComplete="off"
            margin="none"
            id={name}
            label={label}
            onKeyDown={onKeyDown}
            value={formattedDisplayValue}
            error={error}
            inputRef={inputRef}
            style={style}
            InputProps={{
                startAdornment: <span>R$</span>,
                inputProps: {
                    maxLength: maxLength ? maxLength : undefined,
                }
            }}
            {...props}
        />
    );
}

export default PriceTextField;
